import { getRecordReducer, LoadingMessage } from '@stormwater-software/lib-ui';
import { find, get, has } from 'lodash';
import React, {
  PropsWithChildren,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { ToastContainer } from 'react-toastify';
import SuccessNotification from '../components/Shared/SuccessNotification/SuccessNotification';
import userApi from '../utils/api/user';
import xhrService from '../utils/xhrService';
// import { MatomoProvider, createInstance } from '@datapunt/matomo-tracker-react';
import db from '../db/db';

type AppContextValue = {
  state: any;
  initialDataLoaded: boolean;
  set(...args: any[]): any;
  get<V = any>(key: string): V;
  has(key: string): boolean;
  reloadInitialData(): void;
  triggerSuccessNotification(): void;

  getNotificationSetting(notificationType: string): {
    default_email_message?: string;
  };
  loadData<D = any>(cb: () => Promise<D>, what?: string): Promise<D>;
  isLoading: boolean;
  loadingResource: string;
  panel: string;
  setPanel: Function,
};
const AppContext = React.createContext<AppContextValue>(undefined as any);

export function AppStore(props: PropsWithChildren<{}>) {
  const [state, dispatch] = useReducer(getRecordReducer(), {});
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingResource, setLoadingResource] = useState('resource');
  const getPanelVal = localStorage.getItem('navigationPanel');
  const [panel, setPanel] = useState(
    getPanelVal === null ? 'open' : getPanelVal,
  );

  // const matomoInstance = createInstance({
  //   urlBase: 'https://sw2.matomo.cloud',
  //   siteId: 2,
  // });

  /**
   * load initial app data
   *
   * @todo catch errors, store in "globalErrors" key, display them
   */
  const set = (key: string, val: any) =>
    dispatch({
      type: 'mergeDeep',
      payload: { key, val },
    });

  const loadInitialData = async () => {
    // check if it is public url
    const isPublic = window.location.pathname.startsWith('/public');
    let checkForLoggedInData: any = {};
    if (isPublic && window.location.pathname.includes('/projects/')) {
      const projectId = Number(
        window.location.pathname.split('/projects/')[1].split('/')[0],
      );
      checkForLoggedInData.projectId = projectId;
    }
    if (await userApi.isLoggedIn(checkForLoggedInData)) {
      try {
        const appData = await xhrService
          .get('/api/users/profile')
          .then((res: any) => res.data);
        const { appConstants, ...user } = appData;

        try {
          await db.user.put({id:1,user_id: user.id, first_name: user.first_name, last_name: user.last_name, name: user.name, email: user.email,role:user.roleName});
        } catch (dbError) {
          console.warn('IndexedDB write failed (non-fatal):', dbError);
        }

        dispatch({
          type: 'merge',
          payload: {
            user,
            isAdmin:
              user.roleName === 'Admin' || user.roleName === 'Super Admin',
            constants: {
              ...appConstants,
              nonDivisionRoutes: [
                "/divisions",
                "/inspection",
                "/user-manuals",
                "/public/inspection",
                "/administration",
                "/subscription",
              ],
              ccrTemplates: ["Delaware"],
              peTemplates: ["Delaware"],
            },
          },
        });
      } catch (e) {
        userApi.logout();
        setInitialDataLoaded(true);
        window.location.href = `${process.env.REACT_APP_LOGIN_URL}/login`;
      }
    } else {
      // nothing to load
      setInitialDataLoaded(true);

      return Promise.resolve();
    }
  };

  const loadData = async (cb: () => Promise<any>, what?: string) => {
    setLoadingResource(what ? what : '');
    setIsLoading(true);

    try {
      const data = await cb();
      setIsLoading(false);
      return data;
    } catch (e) {
      console.error(e);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    const requiredProps = ['user', 'isAdmin', 'constants'];

    if (
      !initialDataLoaded &&
      requiredProps.every((prop) =>
        Object.prototype.hasOwnProperty.call(state, prop),
      )
    ) {
      setInitialDataLoaded(true);
    }
  }, [state, initialDataLoaded]);

  /**
   * Don't continue unless we have _required_ data
   */
  if (!initialDataLoaded) {
    return <LoadingMessage what='system data' />;
  }

  return (
    <AppContext.Provider
      value={{
        panel,
        setPanel,
        state,
        set,
        get: get.bind(null, state),
        has: (key) =>
          has(state, key) &&
          get(state, key) !== null &&
          get(state, key) !== undefined,

        reloadInitialData: () => loadInitialData(),
        triggerSuccessNotification: () => {
          const toId = window.setTimeout(() => {
            setShowSuccessNotification(false);
          }, 2000);

          setShowSuccessNotification(true);

          return toId;
        },
        getNotificationSetting: (notificationType) : any => {
          return find(get.bind(null, state)('constants.notificationSettings'), {
            name: notificationType,
          });
        },
        initialDataLoaded,
        loadData,
        loadingResource,
        isLoading
      }}
    >
      {' '}
      {props.children}
      {/* <MatomoProvider value={matomoInstance}>{props.children}</MatomoProvider> */}
      <SuccessNotification show={showSuccessNotification} />
      <ToastContainer
        position='top-right'
        autoClose={5000}
        pauseOnFocusLoss={false}
        hideProgressBar
      />
    </AppContext.Provider>
  );
}

export default AppContext;
